/**
 * The advertiser registry: category (confirm unconfirmed ones), agency,
 * aliases, negotiated card, pricing summary. Managers edit inline (PUT
 * /advertisers/:key) — one row at a time, and Cancel asks before dropping
 * changes; everyone can open the pricing-history modal.
 */

import React, { useEffect, useId, useMemo, useState } from 'react';
import { Button, EmptyState, Modal, Spinner, StatusPill, Switch, tableStyles } from '../../ui';
import ChipInput from '../MediaPlans/ChipInput';
import InlineConfirm from '../MediaPlans/InlineConfirm';
import { DIRECT_AGENCY_LABEL, agencyOptions, historyHint } from '../MediaPlans/vocab';
import { getAdvertiserPricingHistory, updateAdvertiser } from '../../../services/mediaPlansApi';
import type { AdvertiserPricingHistory, AdvertiserRecord, MediaPlanAgency, MediaPlanCategory, MediaPlanRateCard, PricingWindowSummary } from '../MediaPlans/types';
import type { Notify } from '../MediaPlans/PlanBuilder';
import { bannerStyle, fieldStyle, fmtDate, fmtInt, fmtUsd, gridStyle, hintStyle, labelStyle, mutedText } from '../MediaPlans/formStyles';

interface Props {
  advertisers: AdvertiserRecord[];
  categories: MediaPlanCategory[];
  agencies: MediaPlanAgency[];
  cards: MediaPlanRateCard[];
  canManage: boolean;
  onUpdated: (record: AdvertiserRecord) => void;
  notify: Notify;
}

interface EditState {
  key: string;
  categoryKey: string;
  agencyKey: string;
  aliases: string[];
  negotiatedRateCardId: string;
  resoldVia: '' | 'revolt' | 'ione';
  parentCompany: string;
  notes: string;
}

const errorMessage = (err: unknown): string => (err instanceof Error ? err.message : 'Something went wrong.');

const monthLabel = (ym: string | undefined): string => {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const WindowRow: React.FC<{ label: string; w: PricingWindowSummary | undefined }> = ({ label, w }) => (
  <tr>
    <td style={tableStyles.td}>{label}</td>
    <td style={tableStyles.tdNum}>{w && w.months > 0 ? fmtUsd(w.weightedEcpm) : <span style={mutedText}>—</span>}</td>
    <td style={tableStyles.tdNum}>{w ? w.months : 0}</td>
    <td style={tableStyles.tdNum}>{w ? fmtInt(w.impressions) : 0}</td>
    <td style={tableStyles.tdNum}>{w ? fmtUsd(w.revenueUsd, 0) : '$0'}</td>
    <td style={{ ...tableStyles.td, ...mutedText }}>{w?.firstMonth ? `${monthLabel(w.firstMonth)} – ${monthLabel(w.lastMonth)}` : ''}</td>
  </tr>
);

const PricingHistoryModal: React.FC<{ advertiser: AdvertiserRecord; onClose: () => void }> = ({ advertiser, onClose }) => {
  const [history, setHistory] = useState<AdvertiserPricingHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    getAdvertiserPricingHistory(advertiser.key)
      .then((h) => alive && setHistory(h))
      .catch((err) => alive && setError(errorMessage(err)));
    return () => {
      alive = false;
    };
  }, [advertiser.key]);
  return (
    <Modal open onClose={onClose} title={`${advertiser.name} — pricing history`} width={760} footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      {error && <div role="alert" style={bannerStyle('danger')}>{error}</div>}
      {!history && !error && (
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
          <Spinner label="Loading history…" />
        </div>
      )}
      {history && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={tableStyles.wrapScroll}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Window</th>
                  <th style={tableStyles.thNum}>Weighted eCPM</th>
                  <th style={tableStyles.thNum}>Months</th>
                  <th style={tableStyles.thNum}>Impressions</th>
                  <th style={tableStyles.thNum}>Revenue</th>
                  <th style={tableStyles.th}>Span</th>
                </tr>
              </thead>
              <tbody>
                <WindowRow label="Last 12 months" w={history.last12} />
                <WindowRow label="All months" w={history.all} />
                <WindowRow label="Direct-sold only" w={history.direct} />
                <WindowRow label="Resold (REVOLT / iOne)" w={history.resold} />
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: 'var(--fg-2)' }}>
            {history.summaryOnly && (
              <span>
                3-yr analysis totals: {fmtUsd(history.summaryOnly.weightedEcpm)} over {history.summaryOnly.months} months ({fmtInt(history.summaryOnly.impressions)} imps)
              </span>
            )}
            {history.category && (
              <span>
                {history.category.label}: {fmtUsd(history.category.weightedEcpm)} across {history.category.advertisers} advertisers (index {Math.round(history.category.indexVsPortfolio * 100)} vs portfolio)
              </span>
            )}
            <span>Portfolio: {fmtUsd(history.portfolioEcpm)}</span>
            {history.dataThrough && <span>Data through {monthLabel(history.dataThrough)}</span>}
          </div>
          {history.contracted.length > 0 && (
            <div>
              <div style={labelStyle}>Contracted rates</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--fg-2)' }}>
                {history.contracted.map((c) => (
                  <li key={`${c.month}-${c.label}`}>
                    {monthLabel(c.month)} · {c.label}
                    {typeof c.cpm === 'number' ? ` · ${fmtUsd(c.cpm)} CPM` : typeof c.flatRate === 'number' ? ` · ${fmtUsd(c.flatRate, 0)} flat` : ''}
                    {c.agency ? ` (${c.agency})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {history.months.length > 0 && (
            <div style={tableStyles.wrapScroll}>
              <table style={tableStyles.table}>
                <thead>
                  <tr>
                    <th style={tableStyles.th}>Month</th>
                    <th style={tableStyles.thNum}>eCPM</th>
                    <th style={tableStyles.thNum}>Impressions</th>
                    <th style={tableStyles.thNum}>Revenue</th>
                    <th style={tableStyles.th}>Booked as</th>
                    <th style={tableStyles.th}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {[...history.months].reverse().slice(0, 36).map((m) => (
                    <tr key={`${m.month}-${m.sourceAdvertiserName}`}>
                      <td style={tableStyles.td}>{monthLabel(m.month)}</td>
                      <td style={tableStyles.tdNum}>{fmtUsd(m.ecpm)}</td>
                      <td style={tableStyles.tdNum}>{fmtInt(m.impressions)}</td>
                      <td style={tableStyles.tdNum}>{fmtUsd(m.revenueUsd, 0)}</td>
                      <td style={tableStyles.td}>
                        {m.sourceAdvertiserName}
                        {m.resoldVia ? <span style={mutedText}> · {m.resoldVia}</span> : null}
                      </td>
                      <td style={{ ...tableStyles.td, ...mutedText }}>{m.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {history.zeroRateMonths.length > 0 && <div style={mutedText}>{history.zeroRateMonths.length} $0 month{history.zeroRateMonths.length === 1 ? '' : 's'} (added value / make-goods) excluded.</div>}
          <div style={mutedText}>Sources: {history.sources.join(', ') || '—'}</div>
        </div>
      )}
    </Modal>
  );
};

const AdvertiserRegistry: React.FC<Props> = ({ advertisers, categories, agencies, cards, canManage, onUpdated, notify }) => {
  const id = useId();
  const [filter, setFilter] = useState('');
  const [onlySuggested, setOnlySuggested] = useState(false);
  const [edit, setEdit] = useState<EditState | null>(null);
  // The edit as it opened, to tell whether Cancel would lose anything.
  const [editOpened, setEditOpened] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyFor, setHistoryFor] = useState<AdvertiserRecord | null>(null);
  const categoryLabel = useMemo(() => new Map(categories.map((c) => [c.key, c.label])), [categories]);
  const agencyName = useMemo(() => new Map(agencies.map((a) => [a.key, a.name])), [agencies]);
  const cardName = useMemo(() => new Map(cards.map((c) => [c.id, c.name])), [cards]);
  const sortedCategories = useMemo(() => [...categories].sort((a, b) => a.label.localeCompare(b.label)), [categories]);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return [...advertisers]
      .filter((a) => !onlySuggested || a.categoryStatus === 'suggested')
      .filter((a) => !q || a.name.toLowerCase().includes(q) || a.aliases.some((al) => al.toLowerCase().includes(q)) || (categoryLabel.get(a.categoryKey) || '').toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [advertisers, filter, onlySuggested, categoryLabel]);

  const startEdit = (a: AdvertiserRecord) => {
    const next: EditState = {
      key: a.key,
      categoryKey: a.categoryKey,
      agencyKey: a.agencyKey || '',
      aliases: [...a.aliases],
      negotiatedRateCardId: a.negotiatedRateCardId || '',
      resoldVia: a.resoldVia || '',
      parentCompany: a.parentCompany || '',
      notes: a.notes || '',
    };
    setEdit(next);
    setEditOpened(JSON.stringify(next));
    setConfirmDiscard(false);
  };

  const cancelEdit = () => {
    if (edit && JSON.stringify(edit) !== editOpened) setConfirmDiscard(true);
    else setEdit(null);
  };

  const save = async (confirmCategory: boolean) => {
    if (!edit) return;
    setSaving(true);
    try {
      const updated = await updateAdvertiser(edit.key, {
        categoryKey: edit.categoryKey,
        ...(confirmCategory ? { categoryStatus: 'confirmed' as const } : {}),
        agencyKey: edit.agencyKey,
        aliases: edit.aliases,
        negotiatedRateCardId: edit.negotiatedRateCardId || null,
        resoldVia: edit.resoldVia || null,
        parentCompany: edit.parentCompany,
        notes: edit.notes,
      });
      onUpdated(updated);
      setEdit(null);
      notify('success', `${updated.name} updated`);
    } catch (err) {
      notify('error', 'Could not save the advertiser', errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmOnly = async (a: AdvertiserRecord) => {
    setSaving(true);
    try {
      const updated = await updateAdvertiser(a.key, { categoryStatus: 'confirmed' });
      onUpdated(updated);
      notify('success', `${updated.name}: category confirmed`);
    } catch (err) {
      notify('error', 'Could not confirm the category', errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const suggestedCount = advertisers.filter((a) => a.categoryStatus === 'suggested').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input style={{ ...fieldStyle, maxWidth: 280 }} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter advertisers…" aria-label="Filter advertisers" />
        <Switch size="sm" checked={onlySuggested} onChange={setOnlySuggested} label={`Only unconfirmed categories${suggestedCount > 0 ? ` (${suggestedCount})` : ''}`} />
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No advertisers match" subtitle={onlySuggested ? 'Every advertiser has a confirmed category.' : 'Try another filter.'} />
      ) : (
        <div style={tableStyles.wrapScroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Advertiser</th>
                <th style={tableStyles.th}>Category</th>
                <th style={tableStyles.th}>Agency</th>
                <th style={tableStyles.th}>Negotiated card</th>
                <th style={tableStyles.thNum}>History</th>
                <th style={tableStyles.th}>Updated</th>
                <th style={tableStyles.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const editing = edit?.key === a.key;
                return (
                  <React.Fragment key={a.key}>
                    <tr style={editing ? tableStyles.trExpanded : undefined}>
                      <td style={tableStyles.td}>
                        <div style={{ fontWeight: 700 }}>{a.name}</div>
                        {a.aliases.length > 0 && <div style={mutedText}>aka {a.aliases.join(', ')}</div>}
                        {a.parentCompany && <div style={mutedText}>{a.parentCompany}</div>}
                      </td>
                      <td style={tableStyles.td}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span>{categoryLabel.get(a.categoryKey) || a.categoryKey}</span>
                          {a.categoryStatus === 'suggested' ? (
                            <StatusPill variant="pending" size="sm" noDot>
                              Unconfirmed
                            </StatusPill>
                          ) : (
                            <span style={mutedText}>{a.categorySource}</span>
                          )}
                        </div>
                        {a.categoryStatus === 'suggested' && a.categoryRationale && <div style={{ ...mutedText, fontSize: 11 }}>{a.categoryRationale}</div>}
                      </td>
                      <td style={tableStyles.td}>{a.agencyKey ? agencyName.get(a.agencyKey) || a.agencyKey : <span style={mutedText}>—</span>}</td>
                      <td style={tableStyles.td}>{a.negotiatedRateCardId ? cardName.get(a.negotiatedRateCardId) || a.negotiatedRateCardId : <span style={mutedText}>—</span>}</td>
                      <td style={tableStyles.tdNum}>
                        <button type="button" style={styles.link} onClick={() => setHistoryFor(a)} aria-label={`Pricing history for ${a.name}: ${historyHint(a.pricingSummary)}`}>
                          {historyHint(a.pricingSummary)}
                        </button>
                      </td>
                      <td style={tableStyles.td}>
                        {fmtDate(a.updatedAt)}
                        <div style={mutedText}>{a.updatedBy?.split('@')[0]}</div>
                      </td>
                      <td style={tableStyles.td}>
                        {canManage && !editing && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            {a.categoryStatus === 'suggested' && (
                              <Button size="sm" variant="subtle" disabled={saving || !!edit} onClick={() => void confirmOnly(a)} aria-label={`Confirm ${a.name}'s category`}>
                                Confirm
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" disabled={saving || !!edit} onClick={() => startEdit(a)} aria-label={`Edit ${a.name}`}>
                              Edit
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {editing && edit && (
                      <tr style={tableStyles.trExpanded}>
                        <td style={tableStyles.td} colSpan={7}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0 8px' }}>
                            <div style={gridStyle}>
                              <div>
                                <label htmlFor={`${id}-cat`} style={labelStyle}>Category</label>
                                <select id={`${id}-cat`} style={{ ...fieldStyle, cursor: 'pointer' }} value={edit.categoryKey} disabled={saving} onChange={(e) => setEdit({ ...edit, categoryKey: e.target.value })}>
                                  {sortedCategories.map((c) => (
                                    <option key={c.key} value={c.key}>{c.label}</option>
                                  ))}
                                </select>
                                <div style={hintStyle}>Changing the category confirms it.</div>
                              </div>
                              <div>
                                <label htmlFor={`${id}-agency`} style={labelStyle}>Agency</label>
                                <select id={`${id}-agency`} style={{ ...fieldStyle, cursor: 'pointer' }} value={edit.agencyKey} disabled={saving} onChange={(e) => setEdit({ ...edit, agencyKey: e.target.value })}>
                                  <option value="">{DIRECT_AGENCY_LABEL}</option>
                                  {agencyOptions(agencies).map((ag) => (
                                    <option key={ag.key} value={ag.key}>{ag.name}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label htmlFor={`${id}-card`} style={labelStyle}>Negotiated rate card</label>
                                <select id={`${id}-card`} style={{ ...fieldStyle, cursor: 'pointer' }} value={edit.negotiatedRateCardId} disabled={saving} onChange={(e) => setEdit({ ...edit, negotiatedRateCardId: e.target.value })}>
                                  <option value="">None</option>
                                  {cards
                                    .filter((c) => c.scope === 'advertiser' && (c.advertiserKey === a.key || c.id === edit.negotiatedRateCardId))
                                    .map((c) => (
                                      <option key={c.id} value={c.id}>{c.name} ({c.status})</option>
                                    ))}
                                </select>
                                <div style={hintStyle}>Advertiser-scope cards for this advertiser. The resolver already prefers them; this is the bookkeeping pointer.</div>
                              </div>
                              <div>
                                <label htmlFor={`${id}-resold`} style={labelStyle}>Resold via</label>
                                <select id={`${id}-resold`} style={{ ...fieldStyle, cursor: 'pointer' }} value={edit.resoldVia} disabled={saving} onChange={(e) => setEdit({ ...edit, resoldVia: e.target.value as EditState['resoldVia'] })}>
                                  <option value="">Direct</option>
                                  <option value="revolt">REVOLT</option>
                                  <option value="ione">iOne</option>
                                </select>
                              </div>
                              <div>
                                <label htmlFor={`${id}-parent`} style={labelStyle}>Parent company</label>
                                <input id={`${id}-parent`} style={fieldStyle} value={edit.parentCompany} maxLength={160} disabled={saving} onChange={(e) => setEdit({ ...edit, parentCompany: e.target.value })} />
                              </div>
                            </div>
                            <ChipInput label="Aliases" values={edit.aliases} onChange={(v) => setEdit({ ...edit, aliases: v })} placeholder="Other names this advertiser books under…" disabled={saving} />
                            <div>
                              <label htmlFor={`${id}-notes`} style={labelStyle}>Notes</label>
                              <textarea id={`${id}-notes`} style={{ ...fieldStyle, minHeight: 52, resize: 'vertical', fontFamily: 'var(--font-sans)' }} value={edit.notes} maxLength={1000} disabled={saving} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
                            </div>
                            {confirmDiscard && (
                              <InlineConfirm
                                title={`Discard your changes to ${a.name}?`}
                                confirmLabel="Discard changes"
                                cancelLabel="Keep editing"
                                tone="danger"
                                onConfirm={() => {
                                  setConfirmDiscard(false);
                                  setEdit(null);
                                }}
                                onCancel={() => setConfirmDiscard(false)}
                              />
                            )}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                              <Button size="sm" variant="ghost" disabled={saving || confirmDiscard} onClick={cancelEdit}>
                                Cancel
                              </Button>
                              {a.categoryStatus === 'suggested' && (
                                <Button size="sm" variant="subtle" disabled={saving} onClick={() => void save(true)}>
                                  Save & confirm category
                                </Button>
                              )}
                              <Button size="sm" variant="primary" disabled={saving} onClick={() => void save(false)}>
                                {saving ? 'Saving…' : 'Save'}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {historyFor && <PricingHistoryModal advertiser={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  link: { border: 'none', background: 'transparent', color: 'var(--fg-link)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, padding: 0, fontFamily: 'var(--font-sans)', fontVariantNumeric: 'tabular-nums' },
};

export default AdvertiserRegistry;
